/**
 * Majority Report — shared protocol.
 *
 * Everything in here is imported by BOTH the server and the client, so it must
 * stay free of any node/browser specific API.
 */
import { QUESTION_OPTION_KEYS as OPTION_KEYS_BY_QUESTION } from './questions.js';
/* ------------------------------------------------------------------ *
 * Tunables
 * ------------------------------------------------------------------ */
export const GAME_CONFIG = {
    /** Hearts a player can start with — the host picks one of these in the lobby. */
    heartOptions: [1, 2, 3],
    defaultHearts: 2,
    /** Fewest players needed to start — the game ends at 2, so 2 is already a win state. */
    minPlayers: 3,
    /** Hard cap so a room stays readable on screen. */
    maxPlayers: 16,
    /** Seconds a player gets to lock in an answer. */
    answerSeconds: 25,
    /** Seconds the reveal stays up before the next round starts automatically. */
    revealSeconds: 12,
    /** Seconds the chosen author gets to write a question in custom mode. */
    writeSeconds: 75,
    /** Suspense beat (ms) between "everyone answered" and the reveal. */
    allAnsweredDelayMs: 700,
    /** Characters allowed in a room code (no O/0/I/1 — they get misheard). */
    roomCodeAlphabet: 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789',
    roomCodeLength: 4,
    nicknameMaxLength: 14,
    /** A room with nobody connected is reaped after this long. */
    emptyRoomTtlMs: 5 * 60 * 1000,
    /**
     * How long a lobby seat is held open after a disconnect. Long enough that a
     * page refresh gets your seat (and your host crown) back, short enough that
     * someone who wandered off does not block the start.
     */
    lobbyGraceMs: 45 * 1000,
    /* Limits on player-written questions. */
    promptMaxLength: 120,
    optionMaxLength: 48,
    /**
     * Every question is a straight two-way split. Deliberately fixed: the whole
     * game is "read the room", and a 50/50 fork forces a real commitment in a way
     * a four-way scatter does not.
     */
    optionCount: 2,
};
/** Emoji the players pick from as their avatar. */
export const AVATARS = [
    '🐻', '🐰', '🦊', '🐼', '🐨', '🐸', '🐥', '🐙',
    '🦄', '🐷', '🐮', '🐧', '🦉', '🐳', '🦋', '🐝',
    '🌸', '🍄', '🍉', '⭐', '☁️', '🌈', '🍡', '🧁',
];
export const CHOICES = ['A', 'B'];
export function otherChoice(choice) {
    return choice === 'A' ? 'B' : 'A';
}
export const GAME_MODES = ['minority-loses', 'majority-loses'];
/* ------------------------------------------------------------------ *
 * Question bank
 *
 * Ids only — the wording lives in the client's locale files. `optionCount`
 * has to live here because the server validates answers against it.
 * ------------------------------------------------------------------ */
export { QUESTION_OPTION_KEYS } from './questions.js';
/** Total questions in the bank. Ids are sparse, so this is not a range. */
export const QUESTION_COUNT = OPTION_KEYS_BY_QUESTION.length;
/** Every question id currently in the bank, ascending. */
export const QUESTION_IDS = OPTION_KEYS_BY_QUESTION.map((entry) => entry.id);
const OPTION_KEYS_BY_ID = new Map(OPTION_KEYS_BY_QUESTION.map((entry) => [entry.id, entry.keys]));
/** The option keys a question offers, or `undefined` if the id is unknown. */
export function optionKeysFor(questionId) {
    return OPTION_KEYS_BY_ID.get(questionId);
}
/**
 * Draw two distinct option keys for a question, in the order they will be
 * shown. Both the pair and its order are random, so a pool of four never
 * settles into the same left/right arrangement twice running.
 */
export function drawOptionPair(questionId, random = Math.random) {
    const pool = OPTION_KEYS_BY_ID.get(questionId);
    if (!pool || pool.length < 2) {
        throw new Error(`question ${questionId} has no drawable option pair`);
    }
    const first = Math.floor(random() * pool.length);
    let second = Math.floor(random() * (pool.length - 1));
    if (second >= first)
        second += 1;
    return [pool[first], pool[second]];
}
/* ------------------------------------------------------------------ *
 * Helpers shared by both sides
 * ------------------------------------------------------------------ */
/** Trim + clamp a nickname. Returns `null` when nothing usable is left. */
export function sanitizeNickname(raw) {
    if (typeof raw !== 'string')
        return null;
    const cleaned = collapse(raw).slice(0, GAME_CONFIG.nicknameMaxLength);
    return cleaned.length > 0 ? cleaned : null;
}
export function isValidMode(value) {
    return value === 'minority-loses' || value === 'majority-loses';
}
export function isValidHearts(value) {
    return (typeof value === 'number' &&
        GAME_CONFIG.heartOptions.includes(value));
}
export function normalizeRoomCode(raw) {
    return typeof raw === 'string' ? raw.toUpperCase().replace(/[^A-Z0-9]/g, '') : '';
}
/** Strip control characters and collapse runs of whitespace. */
function collapse(value) {
    return value
        .replace(/[\u0000-\u001f\u007f]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}
/**
 * Validate and clean a player-written question.
 *
 * Shared so the client can grey out the submit button using exactly the rules
 * the server will enforce — the server still re-runs it, since a client can
 * send anything. Rendering is React text nodes, so escaping is handled there;
 * this is about length, blank entries and duplicates.
 */
export function sanitizeCustomQuestion(rawPrompt, rawOptions) {
    if (typeof rawPrompt !== 'string' || !Array.isArray(rawOptions))
        return null;
    const prompt = collapse(rawPrompt).slice(0, GAME_CONFIG.promptMaxLength);
    if (prompt.length === 0)
        return null;
    const options = rawOptions
        .filter((option) => typeof option === 'string')
        .map((option) => collapse(option).slice(0, GAME_CONFIG.optionMaxLength))
        .filter((option) => option.length > 0);
    if (options.length !== GAME_CONFIG.optionCount)
        return null;
    // Two identical options would make the vote meaningless.
    const seen = new Set(options.map((option) => option.toLowerCase()));
    if (seen.size !== options.length)
        return null;
    return { prompt, options };
}
//# sourceMappingURL=index.js.map